import React, { useMemo, useState } from 'react';
import {
  ROOM_DAY_KEYS,
  ROOM_DAY_LABELS,
  defaultDayPricingFromBase
} from '../../utils/roomDayPricing';
import { roomTaxesFromDiscounted } from '../../utils/roomPricing';

const PRESETS = [
  { id: 'all', label: 'All days', mask: [true, true, true, true, true, true, true] },
  { id: 'mon-thu', label: 'Mon–Thu', mask: [false, true, true, true, true, false, false] },
  { id: 'fri-sat', label: 'Fri–Sat', mask: [false, false, false, false, false, true, true] },
  { id: 'sun', label: 'Sun only', mask: [true, false, false, false, false, false, false] }
];

/**
 * @param {{ price: object, setPrice: (next: object) => void }} props
 */
export default function RoomWeekdayPricingFields({ price, setPrice }) {
  const useBands = Boolean(price?.useDayOfWeekPricing);
  const [templateMrp, setTemplateMrp] = useState(() => Number(price?.mrp) || 0);
  const [templateDp, setTemplateDp] = useState(() => Number(price?.discountedPrice) || 0);
  const [dayMask, setDayMask] = useState(() => PRESETS[0].mask);

  const dayPricing = price?.dayPricing && typeof price.dayPricing === 'object' ? price.dayPricing : {};

  const syncTemplateFromBase = () => {
    setTemplateMrp(Number(price?.mrp) || 0);
    setTemplateDp(Number(price?.discountedPrice) || 0);
  };

  const setDaySlice = (key, field, raw) => {
    const num = field === 'discountedPrice' || field === 'mrp' ? parseFloat(raw) || 0 : raw;
    const prevSlice = dayPricing[key] || {};
    const nextDp =
      field === 'discountedPrice' ? num : Number(prevSlice.discountedPrice ?? price.discountedPrice) || 0;
    setPrice({
      ...price,
      dayPricing: {
        ...dayPricing,
        [key]: {
          ...prevSlice,
          [field]: num,
          taxes: roomTaxesFromDiscounted(nextDp)
        }
      }
    });
  };

  const applyTemplateToMaskedDays = () => {
    const mrp = Number(templateMrp) || 0;
    const dp = Number(templateDp) || 0;
    const taxes = roomTaxesFromDiscounted(dp);
    const next = { ...dayPricing };
    for (let i = 0; i < 7; i += 1) {
      if (!dayMask[i]) continue;
      const k = ROOM_DAY_KEYS[i];
      next[k] = { mrp, discountedPrice: dp, taxes };
    }
    setPrice({
      ...price,
      useDayOfWeekPricing: true,
      dayPricing: next
    });
  };

  const baseDp = Number(price?.discountedPrice) || 0;

  const toggleBandMode = (on) => {
    if (on) {
      const hasAny = ROOM_DAY_KEYS.some((k) => dayPricing[k]?.discountedPrice != null);
      const nextDay =
        hasAny && Object.keys(dayPricing).length > 0
          ? { ...dayPricing }
          : defaultDayPricingFromBase({ mrp: Number(price.mrp) || 0, discountedPrice: baseDp });
      setPrice({
        ...price,
        useDayOfWeekPricing: true,
        dayPricing: nextDay
      });
      syncTemplateFromBase();
    } else {
      setPrice({
        ...price,
        useDayOfWeekPricing: false,
        dayPricing: {}
      });
    }
  };

  const rows = useMemo(
    () =>
      ROOM_DAY_KEYS.map((key, idx) => ({
        key,
        label: ROOM_DAY_LABELS[idx],
        slice: dayPricing[key] || {
          mrp: Number(price?.mrp) || 0,
          discountedPrice: baseDp,
          taxes: roomTaxesFromDiscounted(baseDp)
        }
      })),
    [dayPricing, price, baseDp]
  );

  return (
    <div className="mt-6 space-y-4 border-t border-[#E5E3DF] pt-6">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={useBands}
          onChange={(e) => toggleBandMode(e.target.checked)}
          className="w-4 h-4 rounded border-[#E5E3DF] text-[#FF5E4C] focus:ring-[#FF5E4C]/30"
        />
        <span className="text-[14px] text-[#131313] font-medium">
          Different price by day of week (Mon–Thu vs Fri–Sat vs Sun, etc.)
        </span>
      </label>
      <p className="text-[13px] text-[#78736A] leading-snug">
        Nights are priced by the calendar day&apos;s weekday (UTC date). Per-night overrides on the
        inventory calendar still win over these catalog rates when set. If &quot;price by number of
        adults&quot; is enabled, flat weekday prices below are ignored for bookings with guest
        counts — use the guest pricing section for per-day adult tiers instead.
      </p>

      {useBands && (
        <div className="space-y-5 rounded-[12px] border border-[#E5E3DF] bg-[#FAF7F2]/50 p-4">
          <div>
            <p className="text-[13px] font-medium text-[#131313] mb-2">Quick select weekdays</p>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((pr) => (
                <button
                  key={pr.id}
                  type="button"
                  onClick={() => setDayMask([...pr.mask])}
                  className="px-3 py-1.5 rounded-full text-[12px] font-medium border border-[#E5E3DF] bg-white hover:bg-[#FF5E4C]/10 transition-colors"
                >
                  {pr.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-[12px] font-medium text-[#5c5c5c] mb-1">Template MRP</label>
              <input
                type="number"
                min="0"
                value={templateMrp}
                onChange={(e) => setTemplateMrp(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[10px] text-[14px]"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#5c5c5c] mb-1">
                Template discounted
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={templateDp}
                onChange={(e) => setTemplateDp(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[10px] text-[14px]"
              />
            </div>
            <div className="sm:col-span-2 md:col-span-2 flex flex-wrap gap-2 items-center">
              <span className="text-[12px] text-[#5c5c5c] mr-1">Apply to:</span>
              {ROOM_DAY_LABELS.map((lb, i) => (
                <label
                  key={lb}
                  className="inline-flex items-center gap-1 text-[12px] text-[#131313] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={dayMask[i]}
                    onChange={(e) => {
                      const next = [...dayMask];
                      next[i] = e.target.checked;
                      setDayMask(next);
                    }}
                    className="rounded border-[#E5E3DF]"
                  />
                  {lb}
                </label>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={applyTemplateToMaskedDays}
            className="px-4 py-2 rounded-full bg-[#131313] text-white text-[13px] font-medium hover:bg-black/90 transition-colors"
          >
            Apply template to selected days
          </button>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px] border-collapse min-w-[520px]">
              <thead>
                <tr className="text-[#5c5c5c] border-b border-[#E5E3DF]">
                  <th className="py-2 pr-2 font-medium">Day</th>
                  <th className="py-2 px-2 font-medium">MRP</th>
                  <th className="py-2 px-2 font-medium">Discounted</th>
                  <th className="py-2 pl-2 font-medium">Included tax (18%)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ key, label, slice }) => (
                  <tr key={key} className="border-b border-[#E5E3DF]/80">
                    <td className="py-2 pr-2 font-medium text-[#131313]">{label}</td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        min="0"
                        value={slice.mrp}
                        onChange={(e) => setDaySlice(key, 'mrp', e.target.value)}
                        className="w-full max-w-[120px] px-2 py-1.5 border border-[#E5E3DF] rounded-[8px]"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={slice.discountedPrice}
                        onChange={(e) => setDaySlice(key, 'discountedPrice', e.target.value)}
                        className="w-full max-w-[120px] px-2 py-1.5 border border-[#E5E3DF] rounded-[8px]"
                      />
                    </td>
                    <td className="py-2 pl-2 text-[#5c5c5c]">
                      {roomTaxesFromDiscounted(slice.discountedPrice).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
